import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getClientBufferFlag,
  getSiteBufferDefault,
  listBaysLite,
  listBayBuffers,
  listBlackoutsForGrid,
  listBookingsForGrid,
  listPatternsForGrid,
  listSitesLite,
  getAvailabilityInputs,
} from "@/lib/api";

const NIL_UUID = "00000000-0000-0000-0000-000000000000";

// Sites for the active tenant; keeps the current site if still in the list.
function useSites(viewAsClientId, setSiteId) {
  const [sites, setSites] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listSitesLite(viewAsClientId || null).then(({ data, error }) => {
      if (cancelled) return;
      if (error) toast.error(error.message);
      const list = data || [];
      setSites(list);
      setSiteId((cur) => (cur && list.some((s) => s.id === cur) ? cur : list[0]?.id || ""));
    });
    return () => { cancelled = true; };
  }, [viewAsClientId, setSiteId]);
  return sites;
}

function useBays(siteId) {
  const [bays, setBays] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await listBaysLite(siteId || NIL_UUID, { includeInactive: true });
      if (cancelled) return;
      if (error && siteId) toast.error(error.message);
      setBays(siteId ? (data || []) : []);
    })();
    return () => { cancelled = true; };
  }, [siteId]);
  return bays;
}

// PR-1 buffers: client master switch → site default → per-bay override.
function useBuffers(clientId, siteId) {
  const [clientBufferEnabled, setClientBufferEnabled] = useState(false);
  const [siteBufferDefault, setSiteBufferDefault] = useState(0);
  const [bayBuffers, setBayBuffers] = useState(() => new Map());

  useEffect(() => {
    if (!clientId) { setClientBufferEnabled(false); return undefined; }
    let cancelled = false;
    getClientBufferFlag(clientId).then((flag) => { if (!cancelled) setClientBufferEnabled(flag.enabled); });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    if (!siteId || !clientBufferEnabled) {
      setSiteBufferDefault(0);
      setBayBuffers(new Map());
      return undefined;
    }
    let cancelled = false;
    Promise.all([getSiteBufferDefault(siteId), listBayBuffers(siteId)]).then(([def, buffers]) => {
      if (cancelled) return;
      setSiteBufferDefault(def);
      setBayBuffers(buffers);
    });
    return () => { cancelled = true; };
  }, [siteId, clientBufferEnabled]);

  return { clientBufferEnabled, siteBufferDefault, bayBuffers };
}

function useAvailInputs(siteId) {
  const [availInputs, setAvailInputs] = useState(null);
  useEffect(() => {
    if (!siteId) return undefined;
    let cancelled = false;
    getAvailabilityInputs(siteId, { includeInactive: true }).then((r) => { if (!cancelled) setAvailInputs(r); });
    return () => { cancelled = true; };
  }, [siteId]);
  return availInputs;
}

function useWindowData(siteId, windowStart, windowEnd) {
  const [bookings, setBookings] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!siteId) {
        setBookings([]); setBlackouts([]); setPatterns([]); setLoading(false);
        return;
      }
      const startISO = windowStart.toISOString();
      const endISO   = windowEnd.toISOString();
      const [bk, bo, pt] = await Promise.all([
        listBookingsForGrid(siteId, startISO, endISO),
        listBlackoutsForGrid(siteId, startISO, endISO),
        listPatternsForGrid(siteId, startISO, endISO),
      ]);
      if (cancelled) return;
      if (bk.error) toast.error(bk.error.message);
      setBookings(bk.data || []);
      setBlackouts(bo.data || []);
      setPatterns(pt.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [siteId, windowStart, windowEnd, tick]);

  const refresh = useCallback(() => setTick((k) => k + 1), []);
  return { bookings, blackouts, patterns, loading, refresh };
}

// All server data the Dock Manager grid needs for one site + time window.
export function useDockData({ viewAsClientId, siteId, setSiteId, windowStart, windowEnd }) {
  const sites = useSites(viewAsClientId, setSiteId);
  const activeSite = useMemo(() => sites.find((s) => s.id === siteId) || null, [sites, siteId]);
  const bays = useBays(siteId);
  const buffers = useBuffers(activeSite?.client_id, siteId);
  const availInputs = useAvailInputs(siteId);
  const windowData = useWindowData(siteId, windowStart, windowEnd);
  return { sites, activeSite, bays, availInputs, ...buffers, ...windowData };
}
